import React, { useState } from 'react';
import { BookOpen, GraduationCap, CheckCircle2, ChevronRight } from 'lucide-react';
import { useDemoLang } from './demoLang';

const SYSTEMS = [
  { key: 'anglo', fr: 'Système Anglophone (GCE)', en: 'Anglophone System (GCE)', color: '#0F4C4C' },
  { key: 'franco', fr: 'Système Francophone (BAC)', en: 'Francophone System (BAC)', color: '#c47a2a' },
];

const ANGLO_GRADES = ['Form 1', 'Form 2', 'Form 3', 'Form 4', 'Form 5 (GCE O/L)', 'Lower Sixth', 'Upper Sixth (GCE A/L)'];
const FRANCO_GRADES = ['6ème', '5ème', '4ème', '3ème (BEPC)', '2nde', '1ère (Bac 1)', 'Terminale (Bac 2)'];

const SUBJECTS = [
  { id: 'math',  fr: 'Mathématiques', en: 'Mathematics', icon: '📐' },
  { id: 'phys',  fr: 'Physique',      en: 'Physics',     icon: '⚛️' },
  { id: 'chem',  fr: 'Chimie',        en: 'Chemistry',   icon: '⚗️' },
  { id: 'bio',   fr: 'Biologie / SVT', en: 'Biology',    icon: '🧬' },
  { id: 'en',    fr: 'Anglais',       en: 'English',     icon: '🇬🇧' },
  { id: 'fr',    fr: 'Français',      en: 'French',      icon: '🇫🇷' },
  { id: 'hist',  fr: 'Histoire & Géo', en: 'History & Geography', icon: '🌍' },
  { id: 'ict',   fr: 'Informatique',  en: 'ICT',         icon: '💻' },
  { id: 'tech',  fr: 'Technique / Pro', en: 'Technical / Vocational', icon: '🔧' },
];

const LESSONS = {
  math: {
    fr: {
      title: "Théorème de Pythagore",
      objectives: [
        "Comprendre la relation entre les 3 côtés d'un triangle rectangle",
        "Appliquer la formule a² + b² = c²",
        "Résoudre des problèmes de la vie réelle (rampe, échelle, terrain)",
        "Vérifier si un triangle est rectangle",
      ],
      question: "Un terrain mesure 30 m de long et 40 m de large. Quelle est la longueur de sa diagonale ?",
      answer: "50 m — car 30² + 40² = 900 + 1600 = 2500 = 50².",
    },
    en: {
      title: "Pythagoras' Theorem",
      objectives: [
        "Understand the relationship between the 3 sides of a right triangle",
        "Apply the formula a² + b² = c²",
        "Solve real-world problems (ramp, ladder, plot of land)",
        "Verify whether a triangle is right-angled",
      ],
      question: "A field is 30 m long and 40 m wide. What is the length of its diagonal?",
      answer: "50 m — because 30² + 40² = 900 + 1600 = 2500 = 50².",
    },
  },
  phys: {
    fr: {
      title: "La loi de la gravitation",
      objectives: [
        "Comprendre pourquoi les objets tombent",
        "Calculer le poids avec P = m × g (g ≈ 9.8 N/kg au Cameroun)",
        "Distinguer masse et poids",
        "Appliquer aux situations locales (mangue qui tombe, ballon lancé)",
      ],
      question: "Quel est le poids d'un sac de riz de 25 kg à Yaoundé (g = 9.8 N/kg) ?",
      answer: "P = 25 × 9.8 = 245 N",
    },
    en: {
      title: "Law of Gravitation",
      objectives: [
        "Understand why objects fall",
        "Compute weight using W = m × g (g ≈ 9.8 N/kg in Cameroon)",
        "Tell mass and weight apart",
        "Apply to local scenarios (falling mango, thrown ball)",
      ],
      question: "What is the weight of a 25 kg rice bag in Yaoundé (g = 9.8 N/kg)?",
      answer: "W = 25 × 9.8 = 245 N",
    },
  },
  bio: {
    fr: {
      title: "La photosynthèse",
      objectives: [
        "Identifier les entrées (CO₂, eau, lumière) et les sorties (O₂, glucose)",
        "Localiser la photosynthèse dans la feuille (chloroplaste)",
        "Comprendre le rôle du soleil et de la chlorophylle",
        "Expliquer pourquoi les forêts du Cameroun sont essentielles",
      ],
      question: "Nomme un produit et un réactif de la photosynthèse.",
      answer: "Réactif : CO₂ ou H₂O • Produit : O₂ ou glucose (sucre).",
    },
    en: {
      title: "Photosynthesis",
      objectives: [
        "Identify inputs (CO₂, water, sunlight) and outputs (O₂, glucose)",
        "Locate photosynthesis inside the leaf (chloroplast)",
        "Understand the role of sunlight and chlorophyll",
        "Explain why Cameroon's rainforests matter globally",
      ],
      question: "Name one input and one output of photosynthesis.",
      answer: "Input: CO₂ or H₂O • Output: O₂ or glucose (sugar).",
    },
  },
  hist: {
    fr: {
      title: "La réunification du Cameroun (1961)",
      objectives: [
        "Situer le référendum du 11 février 1961",
        "Distinguer Cameroun britannique et Cameroun français",
        "Comprendre le rôle d'Ahmadou Ahidjo et John Ngu Foncha",
        "Expliquer pourquoi le bilinguisme fait partie de l'identité camerounaise",
      ],
      question: "En quelle année a eu lieu la réunification et qui a signé les accords de Foumban ?",
      answer: "1961. Les accords de Foumban ont été signés par Ahmadou Ahidjo et John Ngu Foncha (juillet 1961).",
    },
    en: {
      title: "Cameroon Reunification (1961)",
      objectives: [
        "Locate the 11 February 1961 referendum",
        "Distinguish British Cameroons from French Cameroun",
        "Understand the roles of Ahmadou Ahidjo and John Ngu Foncha",
        "Explain why bilingualism is core to Cameroonian identity",
      ],
      question: "In what year was Cameroon reunified, and who signed the Foumban accords?",
      answer: "1961. The Foumban accords were signed by Ahmadou Ahidjo and John Ngu Foncha (July 1961).",
    },
  },
  // Sensible defaults for other subjects
  chem: { fr: { title: "Les états de la matière", objectives: ["Identifier solide/liquide/gaz","Décrire les changements d'état","Donner des exemples locaux (eau, glace, vapeur du plantain qui cuit)","Comprendre l'évaporation et la condensation"], question: "Quel changement d'état se produit quand la rosée apparaît le matin ?", answer: "La condensation (gaz → liquide)." }, en: { title: "States of Matter", objectives: ["Identify solid/liquid/gas","Describe state changes","Give local examples (water, ice, steam from plantain cooking)","Understand evaporation and condensation"], question: "What state change occurs when dew forms in the morning?", answer: "Condensation (gas → liquid)." } },
  en:   { fr: { title: "Compréhension écrite — texte narratif", objectives: ["Identifier le narrateur et le cadre","Trouver l'idée principale","Analyser un personnage","Écrire un résumé de 3 phrases"], question: "Read a short passage and identify the setting.", answer: "Guided practice — see teacher script." }, en: { title: "Reading Comprehension — narrative", objectives: ["Identify narrator and setting","Find the main idea","Analyse a character","Write a 3-sentence summary"], question: "Read a short passage and identify the setting.", answer: "Guided practice — see teacher script." } },
  fr:   { fr: { title: "L'accord du participe passé", objectives: ["Reconnaître l'auxiliaire (être / avoir)","Appliquer la règle avec être","Appliquer la règle avec avoir + COD placé avant","Éviter les erreurs classiques"], question: "Complétez : 'Les fleurs que j'ai (cueillir) ce matin sont belles.'", answer: "cueillies (avoir + COD placé avant → accord)." }, en: { title: "Past-participle Agreement", objectives: ["Recognise the auxiliary","Apply the être rule","Apply the avoir rule with pre-placed direct object","Avoid classic mistakes"], question: "Complete: 'Les fleurs que j'ai (cueillir) ce matin sont belles.'", answer: "cueillies (avoir + preceding direct object → agreement)." } },
  ict:  { fr: { title: "Algorithmes de base", objectives: ["Comprendre ce qu'est un algorithme","Écrire un algorithme en pseudo-code","Utiliser 'si / sinon' et 'tant que'","Coder un premier programme en Python"], question: "Écris un algorithme qui affiche 'majeur' si age ≥ 18.", answer: "si age ≥ 18 alors afficher 'majeur' sinon afficher 'mineur'." }, en: { title: "Basic Algorithms", objectives: ["Understand what an algorithm is","Write pseudo-code","Use 'if / else' and 'while'","Code a first Python program"], question: "Write an algorithm that prints 'adult' if age ≥ 18.", answer: "if age ≥ 18 then print 'adult' else print 'minor'." } },
  tech: { fr: { title: "Sécurité en atelier — bases", objectives: ["Identifier les EPI (casque, gants, lunettes)","Utiliser un outil correctement","Réagir en cas d'accident","Respecter la fiche de sécurité"], question: "Nomme 2 équipements de protection individuelle obligatoires.", answer: "Ex : casque, gants, lunettes, chaussures de sécurité." }, en: { title: "Workshop Safety — basics", objectives: ["Identify PPE (helmet, gloves, glasses)","Use a tool correctly","React in case of accident","Follow the safety datasheet"], question: "Name 2 mandatory PPE items.", answer: "e.g. helmet, gloves, safety glasses, safety shoes." } },
};

export default function Curriculum() {
  const { lang, t } = useDemoLang();
  const [system, setSystem] = useState('anglo');
  const [grade, setGrade] = useState('Form 5 (GCE O/L)');
  const [subject, setSubject] = useState('math');

  const grades = system === 'anglo' ? ANGLO_GRADES : FRANCO_GRADES;
  const activeSystem = SYSTEMS.find((s) => s.key === system);
  const lessonPack = LESSONS[subject] || LESSONS.math;
  const lesson = lang === 'fr' ? lessonPack.fr : lessonPack.en;

  return (
    <div className="space-y-5" data-testid="curriculum-page">
      {/* Alignment badge */}
      <section className="rounded-2xl bg-white p-4 border-2 border-teal shadow-squishy" data-testid="curriculum-alignment">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal/15 flex items-center justify-center shrink-0"><GraduationCap className="w-5 h-5 text-teal" /></div>
          <div>
            <div className="font-heading font-bold text-navy text-sm">
              {t("Programme National du Cameroun", "Cameroon National Curriculum")}
            </div>
            <div className="text-navy/70 text-xs mt-0.5">
              {t("Aligné avec le Ministère des Enseignements Secondaires (MINESEC).",
                 "Aligned with the Ministry of Secondary Education (MINESEC).")}
            </div>
          </div>
        </div>
      </section>

      {/* System toggle */}
      <div className="flex gap-2" data-testid="system-toggle">
        {SYSTEMS.map((s) => {
          const active = system === s.key;
          return (
            <button key={s.key} onClick={() => { setSystem(s.key); setGrade(s.key === 'anglo' ? ANGLO_GRADES[4] : FRANCO_GRADES[4]); }}
              className={`flex-1 h-14 rounded-2xl border-2 font-heading font-bold text-sm px-3 transition-colors ${active ? 'text-white shadow-squishyLg' : 'bg-white text-navy border-cream hover:border-teal/60'}`}
              style={active ? { backgroundColor: s.color, borderColor: s.color } : {}}
              data-testid={`system-btn-${s.key}`}>
              {t(s.fr, s.en)}
            </button>
          );
        })}
      </div>

      {/* Grade selector */}
      <div>
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t("Classe", "Grade")}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="grade-selector">
          {grades.map((g) => {
            const active = grade === g;
            return (
              <button key={g} onClick={() => setGrade(g)}
                className={`shrink-0 h-10 px-4 rounded-full font-heading font-bold text-xs transition-colors ${active ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-cream hover:border-teal/60'}`}
                data-testid={`grade-${g.replace(/\s+/g, '-')}`}>{g}</button>
            );
          })}
        </div>
      </div>

      {/* Subject chips */}
      <div>
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
          {t("Matière", "Subject")}
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="subject-grid">
          {SUBJECTS.map((s) => {
            const active = subject === s.id;
            return (
              <button key={s.id} onClick={() => setSubject(s.id)}
                className={`p-2.5 rounded-2xl border-2 text-left transition-colors ${active ? 'bg-teal/10 border-teal' : 'bg-white border-cream hover:border-teal/60'}`}
                data-testid={`subject-${s.id}`}>
                <div className="text-xl">{s.icon}</div>
                <div className="font-heading font-bold text-navy text-[11px] mt-1 leading-tight">{t(s.fr, s.en)}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lesson card */}
      <section className="rounded-3xl bg-white overflow-hidden shadow-squishyLg border-4 border-gold" data-testid="lesson-card">
        <div className="px-5 py-4" style={{ background: activeSystem.color, color: 'white' }}>
          <div className="text-white/80 font-heading font-bold text-[10px] uppercase tracking-wider">
            {activeSystem[lang]} · {grade}
          </div>
          <h3 className="font-heading text-xl font-bold mt-1 leading-tight" data-testid="lesson-title">{lesson.title}</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="font-heading font-bold text-navy text-sm mb-2 inline-flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-teal" /> {t("Objectifs d'apprentissage", "Learning objectives")}
            </div>
            <ul className="space-y-1.5" data-testid="lesson-objectives">
              {lesson.objectives.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-navy text-sm">
                  <CheckCircle2 className="w-4 h-4 text-teal shrink-0 mt-0.5" /><span>{o}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-cream border-2 border-gold p-3">
            <div className="font-heading font-bold text-gold text-[10px] uppercase tracking-wider mb-1">
              {t("Question d'entraînement", "Practice question")}
            </div>
            <div className="text-navy text-sm font-semibold" data-testid="lesson-question">{lesson.question}</div>
            <details className="mt-2">
              <summary className="text-teal text-xs font-heading font-bold cursor-pointer inline-flex items-center gap-1"><ChevronRight className="w-3 h-3" /> {t("Voir la réponse", "Show answer")}</summary>
              <div className="text-navy/80 text-sm mt-1" data-testid="lesson-answer">{lesson.answer}</div>
            </details>
          </div>
        </div>
      </section>
    </div>
  );
}
